import { useCallback } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
} from '@hap/components'
import {
  SCALE_3_POINT,
  SCALE_3_POINT_0_ICON,
  SCALE_3_POINT_0_LABEL,
  SCALE_3_POINT_1_ICON,
  SCALE_3_POINT_1_LABEL,
  SCALE_3_POINT_2_ICON,
  SCALE_3_POINT_2_LABEL,
  SCALE_4_POINT,
  SCALE_4_POINT_1_ICON,
  SCALE_4_POINT_1_LABEL,
  SCALE_4_POINT_2_ICON,
  SCALE_4_POINT_2_LABEL,
  SCALE_4_POINT__1_ICON,
  SCALE_4_POINT__1_LABEL,
  SCALE_4_POINT__2_ICON,
  SCALE_4_POINT__2_LABEL,
  SCALE_5_POINT,
  SCALE_5_POINT_0_ICON,
  SCALE_5_POINT_0_LABEL,
  SCALE_5_POINT_1_ICON,
  SCALE_5_POINT_1_LABEL,
  SCALE_5_POINT_2_ICON,
  SCALE_5_POINT_2_LABEL,
  SCALE_5_POINT_3_ICON,
  SCALE_5_POINT_3_LABEL,
  SCALE_5_POINT_4_ICON,
  SCALE_5_POINT_4_LABEL,
  SCALE_BINARY,
  SCALE_BINARY_MET_ICON,
  SCALE_BINARY_MET_LABEL,
  SCALE_BINARY_NOT_MET_ICON,
  SCALE_BINARY_NOT_MET_LABEL,
} from '@hap/constants'

import { useDatastore } from '../datastore'
import { BaseCommandListProps } from './types'

export function ScorecardCriteriaCommandList({
  placeholder = 'Scorecard Criteria',
  onSelect,
}: BaseCommandListProps<false, string>) {
  const { data, isInitialLoading } = useDatastore(FilterCollection.ScorecardCriteria)

  const handleSelect = useCallback(
    (id: string) => {
      onSelect([id])
    },
    [onSelect],
  )

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput placeholder={placeholder} />
      <CommandList>
        <CommandGroup>
          {data.map((record) => {
            return (
              <CommandItem
                key={record.id}
                value={record.id}
                keywords={[record.name]}
                onSelect={() => handleSelect(record.id)}
              >
                {record.name}
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function ScorecardCriteriaScoreList({
  selected = [],
  onSelect,
  criteriaId,
  multiselect,
}: BaseCommandListProps<false, string> & { criteriaId: string }) {
  const { data } = useDatastore(FilterCollection.ScorecardCriteria)
  const criteria = data?.find((record) => record.id === criteriaId)
  const scale: Nullable<(typeof scaleOptions)[keyof typeof scaleOptions]> = criteria
    ? scaleOptions[criteria.meta.scale as keyof typeof scaleOptions]
    : null

  const handleToggle = useCallback(
    (value: string) => {
      const newSelection = selected.includes(value)
        ? selected.filter((s) => s !== value)
        : [...selected, value]
      onSelect(newSelection)
    },
    [selected, onSelect],
  )

  return (
    <Command>
      <CommandList>
        <CommandGroup>
          {scale?.options.map((option, key) => {
            return (
              <CommandCheckItem
                key={key}
                value={option.value}
                keywords={[option.label]}
                className="group mx-1 flex gap-3"
                selected={selected.includes(option.value)}
                onSelect={() => handleToggle(option.value)}
                multiselect={multiselect}
              >
                <div className="w-6">{option.icon}</div>
                <div>{option.label}</div>
              </CommandCheckItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

const NA_ICON = <div className="text-accent-foreground text-sm font-semibold">N/A</div>

export const scaleOptions = {
  [SCALE_BINARY]: {
    options: [
      {
        icon: SCALE_BINARY_MET_ICON,
        label: SCALE_BINARY_MET_LABEL,
        value: '1',
      },
      {
        icon: SCALE_BINARY_NOT_MET_ICON,
        label: SCALE_BINARY_NOT_MET_LABEL,
        value: '0',
      },
      {
        icon: NA_ICON,
        label: 'Not Applicable',
        value: '{{NULL}}',
      },
    ],
  },
  [SCALE_3_POINT]: {
    options: [
      {
        icon: SCALE_3_POINT_2_ICON,
        label: SCALE_3_POINT_2_LABEL,
        value: '2',
      },
      {
        icon: SCALE_3_POINT_1_ICON,
        label: SCALE_3_POINT_1_LABEL,
        value: '1',
      },
      {
        icon: SCALE_3_POINT_0_ICON,
        label: SCALE_3_POINT_0_LABEL,
        value: '0',
      },
      {
        icon: NA_ICON,
        label: 'Not Applicable',
        value: '{{NULL}}',
      },
    ],
  },
  [SCALE_4_POINT]: {
    options: [
      {
        icon: SCALE_4_POINT_2_ICON,
        label: SCALE_4_POINT_2_LABEL,
        value: '3',
      },
      {
        icon: SCALE_4_POINT_1_ICON,
        label: SCALE_4_POINT_1_LABEL,
        value: '2',
      },
      {
        icon: SCALE_4_POINT__1_ICON,
        label: SCALE_4_POINT__1_LABEL,
        value: '1',
      },
      {
        icon: SCALE_4_POINT__2_ICON,
        label: SCALE_4_POINT__2_LABEL,
        value: '0',
      },
      {
        icon: NA_ICON,
        label: 'Not Applicable',
        value: '{{NULL}}',
      },
    ],
  },
  [SCALE_5_POINT]: {
    options: [
      {
        icon: SCALE_5_POINT_4_ICON,
        label: SCALE_5_POINT_4_LABEL,
        value: '4',
      },
      {
        icon: SCALE_5_POINT_3_ICON,
        label: SCALE_5_POINT_3_LABEL,
        value: '3',
      },
      {
        icon: SCALE_5_POINT_2_ICON,
        label: SCALE_5_POINT_2_LABEL,
        value: '2',
      },
      {
        icon: SCALE_5_POINT_1_ICON,
        label: SCALE_5_POINT_1_LABEL,
        value: '1',
      },
      {
        icon: SCALE_5_POINT_0_ICON,
        label: SCALE_5_POINT_0_LABEL,
        value: '0',
      },
      {
        icon: NA_ICON,
        label: 'Not Applicable',
        value: '{{NULL}}',
      },
    ],
  },
} as const
